import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import type { CharacterControllerState } from './CharacterController';

/**
 * Low-poly character model.
 * Built from basic Three.js geometries — no external assets needed.
 * Animation reads isMoving live from stateRef in useFrame.
 */
export default function CharacterModel({
  stateRef,
}: {
  stateRef: React.MutableRefObject<CharacterControllerState>;
}) {
  const groupRef = useRef<THREE.Group>(null);
  const leftLegRef = useRef<THREE.Mesh>(null);
  const rightLegRef = useRef<THREE.Mesh>(null);
  const leftArmRef = useRef<THREE.Mesh>(null);
  const rightArmRef = useRef<THREE.Mesh>(null);
  const headRef = useRef<THREE.Group>(null);

  // Animation state (refs so useFrame can drive everything)
  const animRef = useRef({ time: 0, blend: 0 });
  const prevTimeRef = useRef(performance.now());

  // Single useFrame — reads isMoving live, drives all animation
  useFrame(() => {
    if (!groupRef.current) return;

    const now = performance.now();
    const delta = Math.min((now - prevTimeRef.current) / 1000, 0.1);
    prevTimeRef.current = now;

    const isMoving = stateRef.current.isMoving;
    const anim = animRef.current;

    // Advance time
    anim.time += delta;

    // Blend toward target (idle=0, walk=1)
    const target = isMoving ? 1 : 0;
    anim.blend += (target - anim.blend) * Math.min(delta * 8, 1);

    const t = anim.time;
    const blend = anim.blend;

    // Body bob
    groupRef.current.position.y = Math.sin(t * 8) * 0.03 * blend;

    // Leg swing
    const swing = Math.sin(t * 8) * 0.5 * blend;
    if (leftLegRef.current) leftLegRef.current.rotation.x = swing;
    if (rightLegRef.current) rightLegRef.current.rotation.x = -swing;

    // Arm swing (opposite to legs)
    const armSwing = Math.sin(t * 8 + Math.PI) * 0.35 * blend;
    if (leftArmRef.current) leftArmRef.current.rotation.x = armSwing;
    if (rightArmRef.current) rightArmRef.current.rotation.x = -armSwing;

    // Head bob
    if (headRef.current) {
      headRef.current.position.y = 1.45 + Math.sin(t * 8 * 0.5) * 0.015 * blend;
    }
  });

  // Colors
  const skinColor = '#F5C6A0';
  const shirtColor = '#FF6B6B';
  const pantsColor = '#2C3E50';
  const shoeColor = '#1a1a2e';
  const backpackColor = '#F5A623';
  const headphoneColor = '#34495e';
  const headphoneCushion = '#e74c3c';

  return (
    <group ref={groupRef}>
      {/* === Body === */}
      <mesh position={[0, 0.65, 0]} castShadow>
        <cylinderGeometry args={[0.25, 0.3, 0.7, 12]} />
        <meshStandardMaterial color={shirtColor} roughness={0.6} />
      </mesh>

      <mesh position={[0, 0.35, 0]} castShadow>
        <sphereGeometry args={[0.28, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color={pantsColor} roughness={0.6} />
      </mesh>

      {/* === Head === */}
      <group ref={headRef} position={[0, 1.45, 0]}>
        <mesh castShadow>
          <sphereGeometry args={[0.22, 16, 12]} />
          <meshStandardMaterial color={skinColor} roughness={0.8} />
        </mesh>
        <mesh position={[-0.08, 0.05, 0.2]}>
          <sphereGeometry args={[0.03, 8, 8]} />
          <meshBasicMaterial color="#111" />
        </mesh>
        <mesh position={[0.08, 0.05, 0.2]}>
          <sphereGeometry args={[0.03, 8, 8]} />
          <meshBasicMaterial color="#111" />
        </mesh>
        <mesh position={[0, -0.02, 0.21]}>
          <torusGeometry args={[0.06, 0.012, 8, 8, Math.PI]} />
          <meshBasicMaterial color="#555" />
        </mesh>
        <mesh position={[0, 0.15, 0]}>
          <torusGeometry args={[0.24, 0.03, 8, 12]} />
          <meshStandardMaterial color={headphoneColor} roughness={0.4} metalness={0.3} />
        </mesh>
        <mesh position={[-0.24, 0.05, 0]}>
          <cylinderGeometry args={[0.06, 0.06, 0.05, 12]} />
          <meshStandardMaterial color={headphoneCushion} roughness={0.3} />
        </mesh>
        <mesh position={[0.24, 0.05, 0]}>
          <cylinderGeometry args={[0.06, 0.06, 0.05, 12]} />
          <meshStandardMaterial color={headphoneCushion} roughness={0.3} />
        </mesh>
      </group>

      {/* === Left Leg === */}
      <group position={[-0.1, 0.35, 0]}>
        <mesh ref={leftLegRef} position={[0, -0.2, 0]} castShadow>
          <cylinderGeometry args={[0.1, 0.12, 0.4, 8]} />
          <meshStandardMaterial color={pantsColor} roughness={0.6} />
        </mesh>
        <mesh position={[0, -0.45, 0.02]} castShadow>
          <boxGeometry args={[0.11, 0.08, 0.18]} />
          <meshStandardMaterial color={shoeColor} roughness={0.7} />
        </mesh>
      </group>

      {/* === Right Leg === */}
      <group position={[0.1, 0.35, 0]}>
        <mesh ref={rightLegRef} position={[0, -0.2, 0]} castShadow>
          <cylinderGeometry args={[0.1, 0.12, 0.4, 8]} />
          <meshStandardMaterial color={pantsColor} roughness={0.6} />
        </mesh>
        <mesh position={[0, -0.45, 0.02]} castShadow>
          <boxGeometry args={[0.11, 0.08, 0.18]} />
          <meshStandardMaterial color={shoeColor} roughness={0.7} />
        </mesh>
      </group>

      {/* === Left Arm === */}
      <group position={[-0.35, 0.85, 0]}>
        <mesh ref={leftArmRef} position={[0, -0.2, 0]} castShadow>
          <cylinderGeometry args={[0.07, 0.08, 0.4, 8]} />
          <meshStandardMaterial color={shirtColor} roughness={0.6} />
        </mesh>
        <mesh position={[0, -0.44, 0]}>
          <sphereGeometry args={[0.06, 8, 8]} />
          <meshStandardMaterial color={skinColor} roughness={0.8} />
        </mesh>
      </group>

      {/* === Right Arm === */}
      <group position={[0.35, 0.85, 0]}>
        <mesh ref={rightArmRef} position={[0, -0.2, 0]} castShadow>
          <cylinderGeometry args={[0.07, 0.08, 0.4, 8]} />
          <meshStandardMaterial color={shirtColor} roughness={0.6} />
        </mesh>
        <mesh position={[0, -0.44, 0]}>
          <sphereGeometry args={[0.06, 8, 8]} />
          <meshStandardMaterial color={skinColor} roughness={0.8} />
        </mesh>
      </group>

      {/* === Backpack === */}
      <group position={[0, 0.7, -0.25]}>
        <mesh castShadow>
          <boxGeometry args={[0.3, 0.35, 0.2]} />
          <meshStandardMaterial color={backpackColor} roughness={0.5} />
        </mesh>
        <mesh position={[0, 0.22, 0.12]}>
          <boxGeometry args={[0.25, 0.05, 0.05]} />
          <meshStandardMaterial color="#e67e22" roughness={0.6} />
        </mesh>
        <mesh position={[0, -0.05, 0.11]}>
          <boxGeometry args={[0.2, 0.15, 0.03]} />
          <meshStandardMaterial color="#e67e22" roughness={0.6} />
        </mesh>
      </group>
    </group>
  );
}
